import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Check, Megaphone, MonitorSmartphone, Target, Users } from 'lucide-react';
import api from '@/lib/axios';
import { useSeo, useJsonLd } from '../hooks/useSeo';
import { useCmsContent } from '../hooks/useCmsContent';
import MarketplaceFooter from '@/components/marketplace/MarketplaceFooter';

const SITE = 'https://marketplacetvde.pt';

const VANTAGENS = [
  { icon: Target, titulo: 'Público certo', texto: 'Quem visita o Marketplace TVDE está a trabalhar ou a entrar no setor: motoristas, frotas e stands.' },
  { icon: MonitorSmartphone, titulo: 'Formatos simples', texto: 'Banners no topo, laterais em ecrã largo e espaços entre resultados, sempre identificados como publicidade.' },
  { icon: Users, titulo: 'Sem pop-ups', texto: 'Não usamos pop-ups, interstitials nem banners fixos que tapem o conteúdo.' },
  { icon: BarChart3, titulo: 'Métricas reais', texto: 'Recebe impressões, cliques e CTR contados no site — sem números inventados.' },
];

const CATEGORIAS = ['oficina', 'seguradora', 'advogado', 'contabilista', 'stand', 'pneus',
  'carregamento', 'formacao', 'associacao', 'outro'];

export default function MarketplaceAdvertisingPage() {
  const { cms } = useCmsContent('paginas');
  const [posicoes, setPosicoes] = useState([]);
  const [form, setForm] = useState({
    nome: '', email: '', telefone: '', empresa: '', categoria: 'oficina',
    posicoes: [], mensagem: '', aceita_privacidade: false,
  });
  const [enviado, setEnviado] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useSeo({
    title: 'Publicidade no Marketplace TVDE — Chegue a motoristas e frotas',
    description:
      'Publicite o seu negócio junto de motoristas e frotas TVDE em Portugal: oficinas, seguradoras, advogados, contabilistas, stands, pneus, carregamento e formação. Pedido de proposta sem compromisso.',
    path: '/publicidade',
  });
  useJsonLd('breadcrumb-publicidade', {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Início', item: `${SITE}/` },
      { '@type': 'ListItem', position: 2, name: 'Publicidade', item: `${SITE}/publicidade` },
    ],
  });

  useEffect(() => {
    api.get('/marketplace/publicidade/posicoes-disponiveis')
      .then((r) => setPosicoes(r.data.items || [])).catch(() => setPosicoes([]));
  }, []);

  const submit = async (ev) => {
    ev.preventDefault();
    setBusy(true); setErr('');
    try {
      await api.post('/marketplace/publicidade/pedido', form);
      try { const { track } = await import('@/lib/analytics'); track('send_contact', { origem: 'publicidade' }); } catch (_) {}
      setEnviado(true);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Não foi possível enviar o pedido. Tente novamente.');
    } finally { setBusy(false); }
  };

  const togglePos = (code) => setForm((f) => ({
    ...f,
    posicoes: f.posicoes.includes(code) ? f.posicoes.filter((c) => c !== code) : [...f.posicoes, code],
  }));

  const input = 'w-full rounded-xl border border-slate-300 px-3 py-2 text-sm';

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="advertising-page">
      <header className="border-b border-slate-200">
        <div className="mx-auto flex w-full max-w-[1100px] items-center justify-between gap-3 px-4 py-4 md:px-8">
          <Link to="/" className="text-sm font-black text-slate-900">Marketplace <span className="text-emerald-600">TVDE</span></Link>
          <Link to="/parceiros" className="text-xs font-semibold text-slate-600 hover:text-emerald-700">Parceiros e serviços</Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1100px] px-4 py-10 md:px-8">
        <p className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-emerald-700">
          <Megaphone className="h-3.5 w-3.5" /> Publicidade
        </p>
        <h1 className="mt-3 text-4xl font-black leading-tight sm:text-5xl lg:text-6xl">
          Chegue a quem vive do TVDE
        </h1>
        <p className="mt-4 max-w-2xl text-base text-slate-600" data-testid="advertising-intro">
          {cms('publicidade_intro') || 'O Marketplace TVDE junta motoristas, frotas e stands em Portugal. Se trabalha com este setor — oficina, seguradora, advogado, contabilista, pneus, carregamento elétrico ou formação — pode apresentar o seu serviço exatamente onde estas pessoas já estão a procurar.'}
        </p>

        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          {VANTAGENS.map(({ icon: Icon, titulo, texto }) => (
            <div key={titulo} className="rounded-2xl border border-slate-200 p-4">
              <Icon className="h-5 w-5 text-emerald-600" />
              <p className="mt-2 text-base font-bold md:text-lg">{titulo}</p>
              <p className="mt-1 text-sm text-slate-600">{texto}</p>
            </div>
          ))}
        </div>

        <section className="mt-10" data-testid="advertising-positions">
          <h2 className="text-base font-bold md:text-lg">Posições disponíveis</h2>
          {posicoes.length === 0 ? (
            <p className="mt-2 text-sm text-slate-600" data-testid="advertising-positions-empty">
              Estamos a preparar os espaços publicitários. Envie o seu pedido abaixo e explicamos as opções e
              condições disponíveis para o seu caso.
            </p>
          ) : (
            <ul className="mt-3 grid gap-2 sm:grid-cols-2">
              {posicoes.map((p) => (
                <li key={p.code} className="flex items-start gap-2 rounded-xl border border-slate-200 p-3 text-sm">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  <span>
                    <strong className="font-bold">{p.label}</strong>
                    <span className="block text-xs text-slate-500">
                      {p.formato} · {p.dimensoes}
                      {p.preco != null ? ` · ${Number(p.preco).toFixed(2)} € + IVA / ${p.duracao_dias} dias` : ' · preço sob proposta'}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-4 md:p-6" data-testid="advertising-form-section">
          <h2 className="text-base font-bold md:text-lg">Pedir proposta</h2>
          <p className="mt-1 text-sm text-slate-600">
            Diga-nos o que quer promover. Respondemos com formatos, disponibilidade e valores.
          </p>

          {enviado ? (
            <p className="mt-4 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800" data-testid="advertising-success">
              Pedido recebido. Entramos em contacto pelo email indicado.
            </p>
          ) : (
            <form onSubmit={submit} className="mt-4 grid gap-3 sm:grid-cols-2" data-testid="advertising-form">
              <input className={input} placeholder="Nome" value={form.nome} required
                onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="advertising-name" />
              <input className={input} type="email" placeholder="Email" value={form.email} required
                onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="advertising-email" />
              <input className={input} placeholder="Telefone (opcional)" value={form.telefone}
                onChange={(e) => setForm({ ...form, telefone: e.target.value })} data-testid="advertising-phone" />
              <input className={input} placeholder="Empresa" value={form.empresa}
                onChange={(e) => setForm({ ...form, empresa: e.target.value })} data-testid="advertising-company" />
              <select className={input} value={form.categoria}
                onChange={(e) => setForm({ ...form, categoria: e.target.value })} data-testid="advertising-category">
                {CATEGORIAS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <textarea className={`${input} sm:col-span-2`} rows={4} placeholder="O que quer promover?"
                value={form.mensagem} onChange={(e) => setForm({ ...form, mensagem: e.target.value })} data-testid="advertising-message" />
              {posicoes.length > 0 && (
                <div className="sm:col-span-2">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Posições que lhe interessam</p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {posicoes.map((p) => (
                      <button key={p.code} type="button" onClick={() => togglePos(p.code)}
                        className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                          form.posicoes.includes(p.code) ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-slate-300 text-slate-600'}`}
                        data-testid={`advertising-pos-${p.code}`}>{p.label}</button>
                    ))}
                  </div>
                </div>
              )}
              <label className="flex items-start gap-2 text-xs text-slate-600 sm:col-span-2">
                <input type="checkbox" checked={form.aceita_privacidade} required
                  onChange={(e) => setForm({ ...form, aceita_privacidade: e.target.checked })} data-testid="advertising-privacy" />
                <span>Confirmo que li a <Link to="/privacidade" className="font-semibold text-emerald-700 underline">Política de Privacidade</Link> e autorizo o contacto sobre este pedido.</span>
              </label>
              {err && <p className="text-xs font-semibold text-red-600 sm:col-span-2" data-testid="advertising-error">{err}</p>}
              <button type="submit" disabled={busy}
                className="rounded-xl bg-emerald-600 px-5 py-3 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:opacity-60 sm:col-span-2"
                data-testid="advertising-submit">
                {busy ? 'A enviar...' : 'Enviar pedido de proposta'}
              </button>
            </form>
          )}
        </section>
      </main>
      <MarketplaceFooter />
    </div>
  );
}
